import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Camera } from "lucide-react";
import { SlideToConfirm } from "@/components/SlideToConfirm";

interface ConfirmDeliveryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deliveryRequestId: string | null;
  // Compressed photo handed up as a blob; the parent uploads it online or
  // queues it offline, so a drop-off in a dead zone still confirms.
  onConfirm: (args: { deliveryId: string; photoBlob: Blob; notes: string }) => void;
  isSubmitting: boolean;
}

export function ConfirmDeliveryDialog({
  open,
  onOpenChange,
  deliveryRequestId,
  onConfirm,
  isSubmitting,
}: ConfirmDeliveryDialogProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [photoBlob, setPhotoBlob] = useState<Blob | null>(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState<string | null>(null);
  const [preparing, setPreparing] = useState(false);
  const [notes, setNotes] = useState("");

  function reset() {
    setPhotoBlob(null);
    setPhotoPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setPreparing(false);
    setNotes("");
  }

  // Modern phone cameras produce 20-40MB JPEGs that hit the upload cap and
  // are gratuitously large for a thumbnail-sized proof photo. Resize on
  // the client to 1600px / q0.8 (~150-300KB) before uploading. Server-side
  // resizeForStorage runs again as a safety net for non-canvas uploads.
  async function compressForUpload(file: File): Promise<File> {
    if (!file.type.startsWith("image/") || file.type === "image/gif") return file;
    const dataUrl: string = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error ?? new Error("read failed"));
      reader.readAsDataURL(file);
    });
    const img: HTMLImageElement = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error("image decode failed"));
      el.src = dataUrl;
    });
    const MAX = 1600;
    let w = img.width;
    let h = img.height;
    if (w > MAX || h > MAX) {
      if (w >= h) {
        h = Math.round(h * (MAX / w));
        w = MAX;
      } else {
        w = Math.round(w * (MAX / h));
        h = MAX;
      }
    }
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(img, 0, 0, w, h);
    const blob: Blob | null = await new Promise((resolve) =>
      canvas.toBlob((b) => resolve(b), "image/jpeg", 0.8),
    );
    if (!blob) return file;
    return new File([blob], file.name.replace(/\.[^.]+$/, "") + ".jpg", {
      type: "image/jpeg",
      lastModified: Date.now(),
    });
  }

  async function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl);
    setPhotoPreviewUrl(URL.createObjectURL(file));
    setPhotoBlob(null);
    setPreparing(true);
    const compressed = await compressForUpload(file).catch(() => file);
    setPhotoBlob(compressed);
    setPreparing(false);
  }

  const canSubmit = Boolean(deliveryRequestId && photoBlob && !preparing && !isSubmitting);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Confirm delivery</DialogTitle>
          <DialogDescription>
            Take a photo showing the drop-off so the buyer knows it was delivered. You need to be within 150m of the delivery address.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={handleFileChange}
          />

          {photoPreviewUrl ? (
            <div className="space-y-2">
              <div className="overflow-hidden rounded-lg border">
                <img src={photoPreviewUrl} alt="Delivery proof" className="block w-full object-cover" />
              </div>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{preparing ? "Preparing photo…" : photoBlob ? "Photo ready" : ""}</span>
                <button
                  type="button"
                  className="text-primary underline-offset-2 hover:underline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={preparing || isSubmitting}
                >
                  Retake
                </button>
              </div>
            </div>
          ) : (
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => fileInputRef.current?.click()}
            >
              <Camera className="mr-2 h-4 w-4" />
              Take photo
            </Button>
          )}

          <Textarea
            placeholder="Optional note for the buyer (e.g. 'Left by front door')"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            maxLength={500}
            rows={3}
          />
        </div>

        <DialogFooter className="flex-col gap-3 sm:flex-col sm:gap-3">
          <SlideToConfirm
            label={canSubmit ? "Slide to confirm delivery" : "Take a photo first"}
            disabled={!canSubmit}
            loading={isSubmitting}
            onConfirm={() => {
              if (!deliveryRequestId || !photoBlob) return;
              onConfirm({ deliveryId: deliveryRequestId, photoBlob, notes: notes.trim() });
            }}
          />
          <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
